<template>
  <v-card class="mx-auto" elevation="6" min-height="44vh">
    <v-list-item>
      <v-list-item-content>
        <v-list-item-title class="d-flex headline primary--text">
          <span class="task-name">[{{ task.business.name }}]</span>
          <v-spacer />
          <v-switch
            class="ma-0 custom-switch"
            :disabled="task.status === 'in_progress' || task.status === 'todo'"
            v-model="task.active"
            @change="activeChanged"
          />
          <v-checkbox
            dense
            class="mt-0 ml-2"
            :ripple="false"
            v-model="isSelected"
            v-if="selectable"
          />
        </v-list-item-title>
        <v-list-item-subtitle>
          {{ task.name }}
        </v-list-item-subtitle>
        <v-progress-linear
          class="mt-2 mb-0"
          :color="getColor(task.status)"
          :height="task.status === 'in_progress' ? 20 : 22"
          :value="
            task.status === 'in_progress'
              ? task.processing_state && task.processing_state.value
                ? task.processing_state.value
                : 0
              : 100
          "
          striped
        >
          <template v-slot="{}">
            <strong v-if="task.status === 'todo'" class="white--text">
              Waiting for execution
            </strong>
            <strong
              v-if="task.status === 'in_progress' && task.processing_state"
              class="white--text"
            >
              {{ task.processing_state.label || 'Task in progress' }}
            </strong>
            <strong v-else-if="task.status === 'failed'" class="white--text">
              Failed
            </strong>
            <strong v-else-if="task.status === 'cloning'" class="white--text">
              Cloning task
            </strong>
            <strong v-else-if="task.status === 'completed'" class="white--text">
              Completed
            </strong>
            <strong
              v-else-if="task.status === 'preprocessorInProgress'"
              class="white--text"
            >
              Preprocessing in progress
            </strong>
          </template>
        </v-progress-linear>
        <v-progress-linear
          v-if="
            task.status === 'in_progress' ||
            task.status === 'cloning' ||
            task.status === 'preprocessorInProgress'
          "
          class="mb-2"
          color="primary"
          height="2"
          indeterminate
        ></v-progress-linear>

        <v-list-item-subtitle>
          <template v-if="task.error && task.status === 'failed'">
            <span v-if="typeof task.error === 'string'" class="error--text">
              {{ task.error }}
            </span>
            <span class="error--text" v-else-if="task.error.message">
              {{ task.error.message }}
            </span>
            <span v-else class="error--text">
              Something went wrong with Matcher system. Please, contact support.
            </span>
          </template>
          <template v-else>
            <span
              class="primary--text"
              v-if="task.status === 'in_progress' || task.status === 'todo'"
            >
              time is calculating...
            </span>
            <span v-else-if="task.status === 'cloning'">
              cloning in progress...
            </span>
            <span v-else-if="task.status === 'preprocessorInProgress'">
              preprocessing in progress...
            </span>
            <span v-else class="primary--text">
              takes {{ getDuration(task.duration) }} min.
            </span>
          </template>
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>

    <v-card-text class="pb-0 pt-2">
      <h3>Input files</h3>
      <v-divider horizontal></v-divider>
    </v-card-text>

    <v-carousel
      hide-delimiter-background
      hide-delimiters
      show-arrows-on-hover
      :show-arrows="task.uploads.length > 1"
      height="50"
      delimiter-icon="mdi-minus"
    >
      <v-carousel-item v-for="file in task.uploads" :key="file.id">
        <v-row dense class="fill-height" align="center" justify="center">
          <v-chip class="ma-2" color="accent" @click="() => openFile(file)">
            <span v-if="file.name.length < 28">{{ file.name }}</span>
            <span v-else>{{ file.name.substring(0, 28) + '..' }}</span>
          </v-chip>
        </v-row>
      </v-carousel-item>
    </v-carousel>

    <v-card-text v-if="task.history && task.history.id" class="pb-0 pt-2">
      <h3>History file</h3>
      <v-divider horizontal></v-divider>
    </v-card-text>
    <v-row dense justify="center">
      <v-chip
        color="accent"
        class="ma-2"
        v-if="task.history && task.history.id"
        @click="() => openFile(task.history)"
      >
        <span v-if="task.history.name.length < 28">
          {{ task.history.name }}
        </span>
        <span v-else>{{ task.history.name.substring(0, 28) + '..' }}</span>
      </v-chip>
    </v-row>

    <v-card-text
      v-if="task.previousRun && task.previousRun.id"
      class="pb-0 pt-2"
    >
      <h3>Previous run file</h3>
      <v-divider horizontal></v-divider>
    </v-card-text>
    <v-row dense justify="center">
      <v-chip
        color="accent"
        class="ma-2"
        v-if="task.previousRun && task.previousRun.id"
        @click="() => openFile(task.previousRun)"
      >
        <span v-if="task.previousRun.name.length < 28">
          {{ task.previousRun.name }}
        </span>
        <span v-else>{{ task.previousRun.name.substring(0, 28) + '..' }}</span>
      </v-chip>
    </v-row>

    <v-card-text
      v-if="task.inventoryFile && task.inventoryFile.id"
      class="pb-0 pt-2"
    >
      <h3>Inventory file</h3>
      <v-divider horizontal></v-divider>
    </v-card-text>
    <v-row dense justify="center">
      <v-chip
        color="accent"
        class="ma-2"
        v-if="task.inventoryFile && task.inventoryFile.id"
        @click="() => openFile(task.inventoryFile)"
      >
        <span v-if="task.inventoryFile.name.length < 28">
          {{ task.inventoryFile.name }}
        </span>
        <span v-else>
          {{ task.inventoryFile.name.substring(0, 28) + '..' }}
        </span>
      </v-chip>
    </v-row>

    <v-card-actions class="task-actions flex-column">
      <v-col class="d-flex pa-0">
        <v-spacer />
        <v-chip
          close
          small
          label
          class="mb-2"
          color="primary"
          :disabled="isLoading"
          @click:close="handleRemoveTag"
          v-if="task.tag"
        >
          <v-icon left>mdi-label</v-icon>
          {{ task.tag.name }}
        </v-chip>
        <v-btn
          text
          small
          class="mb-2"
          color="secondary"
          :disabled="isLoading"
          @click="handleAddTag"
          v-else
        >
          <v-icon left>mdi-label</v-icon>
          Add tag
        </v-btn>
      </v-col>
      <v-col class="d-flex align-center pa-0" xs="12">
        <v-tooltip
          bottom
          :disabled="!isOldLastRun"
          v-if="
            task.status === 'completed' ||
            (task.result_id && task.status !== 'cloning')
          "
        >
          <template v-slot:activator="{ on }">
            <v-btn
              outlined
              :color="isOldLastRun ? 'warning' : 'primary'"
              :loading="isLoading"
              @click="
                $router.push({ path: `/processing-results/${task.result_id}` })
              "
              v-on="on"
            >
              <v-icon left>
                {{
                  isOldLastRun ? 'mdi-clock-alert-outline' : 'mdi-file-export'
                }}
              </v-icon>
              Results
            </v-btn>
          </template>
          <span>The last run of this task was more than 30 days ago.</span>
        </v-tooltip>
        <v-icon v-else>mdi-file-check</v-icon>
        <v-spacer></v-spacer>

        <v-tooltip bottom v-if="task.status !== 'cloning'" color="white">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              class="action-btn"
              color="primary"
              :disabled="isLoading"
              @click="() => copyTask(task)"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
          <span class="black--text">Click to clone this task.</span>
        </v-tooltip>
        <v-tooltip
          bottom
          v-if="
            task.status !== 'completed' &&
            task.status !== 'in_progress' &&
            task.status !== 'cloning'
          "
          color="white"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              class="action-btn"
              color="primary"
              :disabled="isLoading"
              @click="() => reRun(task.id)"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span class="black--text">
            Click to start re-run matching process
          </span>
        </v-tooltip>
        <v-tooltip
          bottom
          v-if="
            !this.$store.getters.isMatcherApiV2Enabled &&
            task.status === 'completed'
          "
          color="white"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              class="action-btn"
              color="primary"
              :disabled="isLoading"
              @click="makeDeepClone"
            >
              <v-icon>mdi-buffer</v-icon>
            </v-btn>
          </template>
          <span class="black--text">Click to make deep clone of the task</span>
        </v-tooltip>
        <v-tooltip bottom color="white">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              icon
              small
              class="action-btn"
              :color="task.comment ? 'success' : 'primary'"
              :disabled="isLoading"
              @click="openComment"
            >
              <v-icon>mdi-comment-text-outline</v-icon>
            </v-btn>
          </template>
          <span class="black--text">
            Click to {{ task.comment ? 'edit' : 'add' }} comment
          </span>
        </v-tooltip>
        <v-tooltip bottom color="white">
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              v-show="task.result_id"
              icon
              small
              class="action-btn"
              color="primary"
              :disabled="isLoading"
              @click="generateLink"
            >
              <v-icon>mdi-share-variant</v-icon>
            </v-btn>
          </template>
          <span class="black--text">Click to generate customer link.</span>
        </v-tooltip>
      </v-col>
    </v-card-actions>
    <TagsDialog ref="tagsDialogRef" @onTagSelected="handleTagSelect" />
  </v-card>
</template>

<script>
import { checkIsLastRunOld } from '@/helpers';
import TagsDialog from '@/components/dialogs/TagsDialog';
import { tasksApiV1 } from '@/api/v1';
import { tasksApiV2 } from '@/api/v2';

export default {
  name: 'TaskElement',
  props: {
    task: {
      type: Object,
      default: () => ({
        result: {
          statistics: {}
        }
      })
    },
    selectable: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isOldLastRun: false,
    isLoading: false,
    isSelected: false
  }),
  created() {
    this.isOldLastRun = checkIsLastRunOld(this.task.result);
  },
  components: {
    TagsDialog
  },
  watch: {
    isSelected() {
      this.handleTaskSelection();
    }
  },
  methods: {
    getColor(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'error';
        case 'in_progress':
        case 'cloning':
        case 'preprocessorInProgress':
          return 'primary';
        case 'todo':
          return 'accent';
      }
    },
    openFile(file) {
      window.open(file.location, '_blank');
    },
    generateLink() {
      this.$emit('generateLink', this.task.result);
    },
    getDuration(duration) {
      return ((duration || 0) / 60000).toFixed(2);
    },
    reRun(task_id) {
      this.$emit('onReRun', task_id);
    },
    copyTask(task) {
      this.$emit('onCopyTask', task);
    },
    activeChanged() {
      const { id: taskId, active } = this.task;

      this.$emit('onTaskActiveChanged', { taskId, active });
    },
    openComment() {
      const { id: taskId, comment, business, name } = this.task;
      const taskFullName = `${business.name}-${name}`;

      this.$emit('onOpenTaskComment', { taskId, comment, name: taskFullName });
    },
    makeDeepClone() {
      this.$emit('onDeepCloneTask', this.task);
    },
    handleAddTag() {
      this.$refs.tagsDialogRef.open(true);
    },
    async handleTagSelect(tagId) {
      this.isLoading = true;
      await (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      ).updateTask(this.task.id, { tag_id: tagId });
      this.isLoading = false;
    },
    async handleRemoveTag() {
      this.isLoading = true;
      await (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      ).updateTask(this.task.id, { tag_id: null });
      this.isLoading = false;
    },
    handleTaskSelection() {
      this.$emit('onTaskSelected', this.task.id, this.isSelected);
    }
  }
};
</script>

<style lang="scss">
$activeSwitcherWidth: 41px;

.task-actions {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.task-name {
  padding-right: 5px;
  max-width: calc(100% - #{$activeSwitcherWidth});
  text-overflow: ellipsis;
  overflow: hidden;
}

.custom-switch {
  max-width: $activeSwitcherWidth;

  .v-input--selection-controls__ripple {
    display: none;
  }
}

.action-btn {
  margin: 0 2px;
}

.tag-skeleton {
  border: 1px dashed #eeeeee;
}
</style>
